import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  type AspTabChangeMessage,
  type HeightChangeMessage,
  buildAspTabChangeMessage,
  buildHeightChangeMessage,
  getWidgetDocumentHeight,
  getWidgetPageRoot,
  getWidgetParentOrigin,
} from '@utils/widgetMessages';

const HEIGHT_SETTLE_MS = 300;

export interface WidgetHeightPostMessageOptions {
  enabled: boolean;
  isPageSettled: boolean;
}

export const useWidgetHeightPostMessage = ({
  enabled,
  isPageSettled,
}: WidgetHeightPostMessageOptions): void => {
  const location = useLocation();
  const lastHeightMessageKeyRef = useRef<string>('');
  const lastTabChangeKeyRef = useRef<string>('');

  useEffect(() => {
    if (!enabled || !isPageSettled || typeof window === 'undefined') {
      return undefined;
    }

    const hasParentWindow = window.parent !== window;
    const targetOrigin = hasParentWindow ? getWidgetParentOrigin() : '*';
    let settleTimerId: number | null = null;
    let candidateHeight = 0;
    const locationSnapshot = {
      pathname: location.pathname,
      search: location.search,
    };
    const emitMessage = (message: AspTabChangeMessage | HeightChangeMessage) => {
      console.log('[eSmart postMessage]', message);
      if (hasParentWindow) {
        window.parent.postMessage(message, targetOrigin);
      }
    };

    const postTabChange = () => {
      const message = buildAspTabChangeMessage(locationSnapshot);
      const messageKey = JSON.stringify(message);

      if (messageKey === lastTabChangeKeyRef.current) {
        return;
      }

      lastTabChangeKeyRef.current = messageKey;
      emitMessage(message);
    };

    const postHeight = (height: number) => {
      const message = buildHeightChangeMessage(locationSnapshot, height);
      const messageKey = JSON.stringify(message);

      if (messageKey === lastHeightMessageKeyRef.current) {
        return;
      }

      lastHeightMessageKeyRef.current = messageKey;
      emitMessage(message);
    };

    const cancelStablePost = () => {
      if (settleTimerId === null) return;

      window.clearTimeout(settleTimerId);
      settleTimerId = null;
    };

    const startConfirmationTimer = () => {
      cancelStablePost();
      settleTimerId = window.setTimeout(() => {
        settleTimerId = null;
        const confirmedHeight = getWidgetDocumentHeight();

        if (confirmedHeight !== candidateHeight) {
          candidateHeight = confirmedHeight;
          startConfirmationTimer();
          return;
        }

        postHeight(confirmedHeight);
        postTabChange();
      }, HEIGHT_SETTLE_MS);
    };

    const scheduleStablePost = () => {
      candidateHeight = getWidgetDocumentHeight();
      startConfirmationTimer();
    };

    window.addEventListener('load', scheduleStablePost);
    window.addEventListener('resize', scheduleStablePost);

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(scheduleStablePost)
      : null;
    if (resizeObserver) {
      const pageRoot = getWidgetPageRoot();
      if (pageRoot) resizeObserver.observe(pageRoot);
      resizeObserver.observe(document.documentElement);
      if (document.body) resizeObserver.observe(document.body);
    }

    scheduleStablePost();

    return () => {
      cancelStablePost();
      window.removeEventListener('load', scheduleStablePost);
      window.removeEventListener('resize', scheduleStablePost);
      resizeObserver?.disconnect();
    };
  }, [enabled, isPageSettled, location.pathname, location.search]);
};
